<script setup lang="ts">
import { map, sumBy } from "lodash-es";
import moment from "moment-timezone";
import { useFormat } from "~/composables/use-format";

defineOptions({ name: "WorkspaceReport" });

const { t } = useI18n();
const { c, n } = useFormat();
const { $api } = useNuxtApp();
const route = useRoute();
const toast = useToast();
const { set: setTitle } = usePageTitle();

interface SubscriptionSeriesRecord {
  id: string;
  label: string;
  start?: number;
  signups: number;
  newCustomers: number;
  churnedCustomers: number;
  churnRate: number;
  newMRR: number;
  expansionMRR: number;
  contractionMRR: number;
  churnedMRR: number;
  mrr: number;
}
interface BreakdownRecord {
  id: string;
  label: string;
  count: number;
}
interface WorkspaceAnalytics {
  overview: { total: number };
  series: BreakdownRecord[];
}
interface SubscriptionAnalytics {
  overview: {
    currency: string;
    activeCustomers: number;
    overdueCustomers: number;
    churnRate: number;
    mrr: number;
    arpa: number;
  };
  series: SubscriptionSeriesRecord[];
}

const tabs = computed(() => [
  { value: "workspaces", label: t("analytics.tabWorkspaces") },
  { value: "subscriptions", label: t("analytics.tabSubscriptions") },
]);
const activeTab = computed({
  get: () => {
    const tab = String(route.query.tab || "workspaces");
    return tabs.value.some((tb) => tb.value === tab) ? tab : "workspaces";
  },
  set: (tab) => navigateTo({ query: { ...route.query, tab } }),
});

const timezone = "Asia/Ho_Chi_Minh";
// Matches the picker's "Last 30 days" preset exactly, so the field shows
// that label and leaves its row active.
const periodFilter = reactive({
  from: moment.tz(timezone).subtract(29, "days").startOf("day").unix(),
  to: moment.tz(timezone).endOf("day").unix(),
  groupBy: "week",
});
const query = computed(() => ({
  ...(periodFilter.from ? { from: periodFilter.from } : {}),
  ...(periodFilter.to ? { to: periodFilter.to } : {}),
  groupBy: periodFilter.groupBy,
}));
const groupByItems = computed(() => [
  { value: "week", label: t("analytics.groupBy.week") },
  { value: "month", label: t("analytics.groupBy.month") },
  { value: "year", label: t("analytics.groupBy.year") },
]);

const firstLoading = ref(true);
// firstLoading only ever falls once; this one tracks every refetch too, so a
// filter change shows the charts working instead of sitting on stale numbers.
const loading = ref(false);
const data = ref<SubscriptionAnalytics | null>(null);
const byPlan = ref<BreakdownRecord[]>([]);
const byStatus = ref<BreakdownRecord[]>([]);
// Both donuts split the same live population, so one total describes both.
const liveTotal = ref(0);

// Colour is pinned per status so a slice keeps its meaning as counts shift.
// Canvas cannot resolve `var(--token)`, so these mirror the semantic fills as
// literals — same values the chart theme's palette is built from.
const statusStyles: Record<string, { key: string; color: string }> = {
  active: { key: "statusActive", color: "#0ecc5d" },
  trialing: { key: "statusTrialing", color: "#0087ff" },
  "past-due": { key: "statusPastDue", color: "#ffce00" },
  cancelled: { key: "statusCancelled", color: "#e54d2e" },
  free: { key: "statusFree", color: "#c8c8c8" },
};

const newCount = computed(() => sumBy(data.value?.series ?? [], "newCustomers"));
const churnedCount = computed(() => sumBy(data.value?.series ?? [], "churnedCustomers"));
const newMRRTotal = computed(() => sumBy(data.value?.series ?? [], "newMRR"));
const lostMRRTotal = computed(() => sumBy(data.value?.series ?? [], "churnedMRR"));
const labels = computed(() => map(data.value?.series ?? [], "label"));

const customersChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: { data: [t("analytics.chart.newCustomers"), t("analytics.chart.churnedCustomers")] },
  xAxis: { type: "category", data: labels.value },
  yAxis: { type: "value", minInterval: 1 },
  series: [
    { name: t("analytics.chart.newCustomers"), type: "bar", data: map(data.value?.series ?? [], "newCustomers") },
    { name: t("analytics.chart.churnedCustomers"), type: "bar", data: map(data.value?.series ?? [], (m) => -m.churnedCustomers) },
  ],
}));

// Slices carry no labels, so the legend is what names them — a theme cannot
// add one, the option has to ask for it.
const planChartOption = computed(() => ({
  tooltip: { trigger: "item" },
  legend: {},
  series: [{ type: "pie", data: map(byPlan.value, (p) => ({ name: p.label, value: p.count })) }],
}));

const statusChartOption = computed(() => ({
  tooltip: { trigger: "item" },
  legend: {},
  series: [
    {
      type: "pie",
      data: map(byStatus.value, (s) => ({
        name: t(`workspace.subscription.${statusStyles[s.id]?.key ?? "statusFree"}`),
        value: s.count,
        itemStyle: { color: statusStyles[s.id]?.color },
      })),
    },
  ],
}));

const mrrChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  xAxis: { type: "category", data: labels.value },
  yAxis: { type: "value" },
  series: [
    {
      name: t("analytics.chart.mrr"),
      type: "line",
      areaStyle: { opacity: 0.08 },
      data: map(data.value?.series ?? [], (m) => Math.round(m.mrr)),
    },
  ],
}));

const waterfallChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: {
    data: [
      t("analytics.chart.newMRR"),
      t("analytics.chart.expansionMRR"),
      t("analytics.chart.contractionMRR"),
      t("analytics.chart.churnedMRR"),
    ],
  },
  xAxis: { type: "category", data: labels.value },
  yAxis: { type: "value" },
  series: [
    { name: t("analytics.chart.newMRR"), type: "bar", stack: "mrr", data: map(data.value?.series ?? [], (m) => Math.round(m.newMRR)) },
    { name: t("analytics.chart.expansionMRR"), type: "bar", stack: "mrr", data: map(data.value?.series ?? [], (m) => Math.round(m.expansionMRR)) },
    { name: t("analytics.chart.contractionMRR"), type: "bar", stack: "mrr", data: map(data.value?.series ?? [], (m) => -Math.round(m.contractionMRR)) },
    { name: t("analytics.chart.churnedMRR"), type: "bar", stack: "mrr", data: map(data.value?.series ?? [], (m) => -Math.round(m.churnedMRR)) },
  ],
}));

async function load() {
  loading.value = true;
  try {
    const [trend, plan, status] = await Promise.all([
      $api<SubscriptionAnalytics>("/analytics/subscriptions", { query: query.value }),
      $api<WorkspaceAnalytics>("/analytics/workspaces", { query: { groupBy: "plan" } }),
      $api<WorkspaceAnalytics>("/analytics/workspaces", { query: { groupBy: "status" } }),
    ]);
    data.value = trend;
    byPlan.value = plan.series;
    byStatus.value = status.series;
    liveTotal.value = plan.overview.total;
    firstLoading.value = false;
  } catch {
    toast.error({ title: t("message.loadFailed") });
  } finally {
    loading.value = false;
  }
}

watch(query, () => {
  load();
});

onMounted(async () => {
  setTitle(t("analytics.workspacesTitle"));
  await load();
});
</script>

<template>
  <div class="flex flex-col gap-medium">
    <Tabs v-model="activeTab" :items="tabs">
      <template #workspaces>
        <div class="flex flex-col gap-xsmall md:flex-row md:items-center md:justify-between">
          <SegmentedControl v-model="periodFilter.groupBy" :items="groupByItems" size="small" />
          <FormDateRange
            v-model:from="periodFilter.from"
            v-model:to="periodFilter.to"
            :timezone="timezone"
            size="sm"
            class="w-full md:w-auto"
          />
        </div>

        <div class="grid grid-cols-2 gap-medium md:grid-cols-3 lg:grid-cols-5">
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.active')"
            :value="n(data?.overview.activeCustomers ?? 0, 'integer')"
          />
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.newCount')"
            :value="n(newCount, 'integer')"
          />
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.churnedCount')"
            :value="n(churnedCount, 'integer')"
          />
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.overdue')"
            :value="n(data?.overview.overdueCustomers ?? 0, 'integer')"
            :intent="(data?.overview.overdueCustomers ?? 0) > 0 ? 'negative' : 'neutral'"
          />
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.churnRate')"
            :value="n((data?.overview.churnRate ?? 0) / 100, 'percent')"
          />
        </div>

        <SectionCard :title="t('analytics.chart.newVsChurned')">
          <Chart :option="customersChartOption" :loading="loading" />
        </SectionCard>

        <!-- Both split the customer base as it stands today, so neither moves
             with the date range above — the description has to say so. -->
        <div class="grid grid-cols-1 gap-medium lg:grid-cols-2">
          <SectionCard
            :title="t('analytics.chart.byPlan')"
            :description="t('analytics.chart.liveSplit', { count: n(liveTotal, 'integer') })"
          >
            <Chart :option="planChartOption" :loading="loading" />
          </SectionCard>
          <SectionCard
            :title="t('analytics.chart.subscriptionStatus')"
            :description="t('analytics.chart.liveSplit', { count: n(liveTotal, 'integer') })"
          >
            <Chart :option="statusChartOption" :loading="loading" />
          </SectionCard>
        </div>

        <Card v-if="firstLoading || data?.series.length">
          <DataTable
            :columns="[
              { key: 'label', label: t('analytics.field.period'), class: 'min-w-[112px]' },
              { key: 'signups', label: t('analytics.chart.signups'), align: 'right', class: 'min-w-[112px]' },
              { key: 'newCustomers', label: t('analytics.kpi.newCount'), align: 'right', class: 'min-w-[112px]' },
              { key: 'churnedCustomers', label: t('analytics.kpi.churnedCount'), align: 'right', class: 'min-w-[112px]' },
              { key: 'churnRate', label: t('analytics.kpi.churnRate'), align: 'right', class: 'min-w-[112px]' },
            ]"
            :items="data?.series ?? []"
            row-key="id"
          >
            <template #cell-label="{ row }">
              <span class="content-neutral-strong">{{ row.label }}</span>
            </template>
            <template #cell-signups="{ row }">{{ n(row.signups, "integer") }}</template>
            <template #cell-newCustomers="{ row }">{{ n(row.newCustomers, "integer") }}</template>
            <template #cell-churnedCustomers="{ row }">{{ n(row.churnedCustomers, "integer") }}</template>
            <template #cell-churnRate="{ row }">{{ n(row.churnRate / 100, "percent") }}</template>
          </DataTable>
        </Card>
      </template>

      <template #subscriptions>
        <div class="flex flex-col gap-xsmall md:flex-row md:items-center md:justify-between">
          <SegmentedControl v-model="periodFilter.groupBy" :items="groupByItems" size="small" />
          <FormDateRange
            v-model:from="periodFilter.from"
            v-model:to="periodFilter.to"
            :timezone="timezone"
            size="sm"
            class="w-full md:w-auto"
          />
        </div>

        <div class="grid grid-cols-2 gap-medium md:grid-cols-4">
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.mrr')"
            :value="c(data?.overview.mrr ?? 0, 'USD')"
          />
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.newMRR')"
            :value="c(newMRRTotal, 'USD')"
            intent="positive"
          />
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.lostMRR')"
            :value="c(lostMRRTotal, 'USD')"
            :intent="lostMRRTotal > 0 ? 'negative' : 'neutral'"
          />
          <MetricCard
            :loading="firstLoading"
            :label="t('analytics.kpi.arpa')"
            :value="c(data?.overview.arpa ?? 0, 'USD')"
          />
        </div>

        <div class="grid grid-cols-1 gap-medium lg:grid-cols-2">
          <SectionCard :title="t('analytics.chart.mrr')">
            <Chart :option="mrrChartOption" :loading="loading" />
          </SectionCard>
          <SectionCard :title="t('analytics.chart.mrrMovements')">
            <Chart :option="waterfallChartOption" :loading="loading" />
          </SectionCard>
        </div>

        <Card v-if="firstLoading || data?.series.length">
          <DataTable
            :columns="[
              { key: 'label', label: t('analytics.field.period'), class: 'min-w-[112px]' },
              { key: 'newMRR', label: t('analytics.chart.newMRR'), align: 'right', class: 'min-w-[144px]' },
              { key: 'expansionMRR', label: t('analytics.chart.expansionMRR'), align: 'right', class: 'min-w-[144px] hidden md:table-cell' },
              { key: 'contractionMRR', label: t('analytics.chart.contractionMRR'), align: 'right', class: 'min-w-[144px] hidden md:table-cell' },
              { key: 'churnedMRR', label: t('analytics.chart.churnedMRR'), align: 'right', class: 'min-w-[144px]' },
              { key: 'mrr', label: t('analytics.chart.mrr'), align: 'right', class: 'min-w-[144px]' },
            ]"
            :items="data?.series ?? []"
            row-key="id"
          >
            <template #cell-label="{ row }">
              <span class="content-neutral-strong">{{ row.label }}</span>
            </template>
            <template #cell-newMRR="{ row }">
              <span class="content-positive">{{ c(row.newMRR, "USD") }}</span>
            </template>
            <template #cell-expansionMRR="{ row }">
              <span class="content-positive">{{ c(row.expansionMRR, "USD") }}</span>
            </template>
            <template #cell-contractionMRR="{ row }">
              <span class="content-neutral-medium">{{ c(row.contractionMRR, "USD") }}</span>
            </template>
            <template #cell-churnedMRR="{ row }">
              <span :class="row.churnedMRR > 0 ? 'content-negative' : 'content-neutral-medium'">
                {{ c(row.churnedMRR, "USD") }}
              </span>
            </template>
            <template #cell-mrr="{ row }">
              <span class="content-neutral-strong">{{ c(row.mrr, "USD") }}</span>
            </template>
          </DataTable>
        </Card>
      </template>
    </Tabs>
  </div>
</template>

<style scoped lang="scss">
:deep(.tabs__content) {
  padding: token("padding-medium") 0;
}

:deep(.tabs__content > div) {
  display: flex;
  flex-direction: column;
  gap: token("gap-medium");
}
</style>
